import { Menu, MenuTrigger, MenuTriggerItem, MenuList, MenuButton } from '@silo/ui';
import { SiloAsset } from 'queries/decoratedSilo';
import { FC, useMemo } from 'react';
import { AssetLogo } from '../icons/AssetLogo';
import { ChecksumAddress, ProtocolInstance, isChecksumAddress } from '@silo/types';

export const useSelectedAsset = (assets: SiloAsset[], selectedAddress: ChecksumAddress): SiloAsset =>
  useMemo(
    () => assets.find(({ assetAddress }) => assetAddress === selectedAddress) || assets[0],
    [assets, selectedAddress]
  );

interface AssetMenuProps {
  protocolInstance: ProtocolInstance;
  assets: SiloAsset[];
  selectedAddress: ChecksumAddress;
  setSelectedAddress: (address: ChecksumAddress) => void;
  onSelect?: () => void;
  disabled?: boolean;
  dataCyPrefix?: string;
  asButton?: boolean;
}

export const AssetMenu: FC<AssetMenuProps> = ({
  assets,
  selectedAddress,
  setSelectedAddress,
  protocolInstance,
  onSelect,
  disabled,
  dataCyPrefix,
  asButton = false,
}) => {
  const selectedAsset = useSelectedAsset(assets, selectedAddress);
  const prefix = dataCyPrefix ? `${dataCyPrefix}-` : '';

  if (!assets.length) return <></>;

  const setSelectedId = (id: string) => {
    if (isChecksumAddress(id)) {
      setSelectedAddress(id);
    }
  };

  return (
    <Menu
      id={`${prefix}asset-menu`}
      selectedId={selectedAddress}
      setSelectedId={setSelectedId}
      onSelect={onSelect}
      disabled={disabled}
    >
      {asButton ? (
        <MenuButton variant="form" className="w-full" data-cy={`${prefix}asset-menu`}>
          <AssetDisplay asset={selectedAsset} protocolInstance={protocolInstance} />
        </MenuButton>
      ) : (
        <MenuTrigger data-cy={`${prefix}asset-menu`}>
          <AssetDisplay asset={selectedAsset} protocolInstance={protocolInstance} />
        </MenuTrigger>
      )}

      <MenuList>
        {assets.map((asset) => (
          <MenuTriggerItem id={asset.assetAddress} key={asset.assetAddress} data-cy={`${prefix}asset-menu-item`}>
            <AssetDisplay asset={asset} protocolInstance={protocolInstance} />
          </MenuTriggerItem>
        ))}
      </MenuList>
    </Menu>
  );
};

interface AssetDisplayProps {
  asset: SiloAsset;
  protocolInstance: ProtocolInstance;
}

const AssetDisplay: FC<AssetDisplayProps> = ({ asset: { assetAddress, symbol }, protocolInstance }) => (
  <div className="flex items-center gap-x-2">
    <AssetLogo address={assetAddress} protocolInstance={protocolInstance} size="md" />
    <span className="text-base sm:text-xl whitespace-nowrap">{symbol}</span>
  </div>
);
